import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`text-sm font-semibold transition-colors ${
                pathname === l.to ? "text-orange-600" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="nav-call-button"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:border-orange-300 hover:text-orange-600"
          >
            <Phone className="h-4 w-4 text-orange-600" />
            {PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            data-testid="nav-quote-button"
            className="rounded-full bg-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-orange-600/30 transition-colors hover:bg-orange-700"
          >
            Get Free Quote
          </Link>
        </div>
        <button
          className="md:hidden p-2 text-slate-800"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-button"
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <nav className="px-4 py-4 flex flex-col gap-2">
              {LINKS.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={`rounded-xl px-3 py-2.5 text-base font-semibold ${
                    pathname === l.to ? "text-orange-600 bg-orange-50" : "text-slate-700"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-button"
                className="mt-2 flex items-center justify-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-base font-semibold text-slate-800"
              >
                <Phone className="h-4 w-4 text-orange-600" />
                Call {PHONE_DISPLAY}
              </a>
              <Link
                to="/contact"
                data-testid="mobile-quote-button"
                className="rounded-full bg-orange-600 px-5 py-3 text-base font-semibold text-white text-center"
              >
                Get Free Quote
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
